/**
 * Reader Mode — distraction-free reading with highlights
 *
 * Exposes broad globs — `*.html`, `js/*`, `css/*`, `fonts/*`, `img/*` — to <all_urls>
 * with no `use_dynamic_url`, so every bundled file resolves from any page at a fixed
 * URL. The icon set under `img/icons/` and the top-level `main.html` are stable paths.
 *
 * The 128px icon is the cleanest signal: an image loaded via `probeImage` with no CORS
 * grant, served only when the extension is installed.
 *
 * Manifest verified against version 2.0.9.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'llimhhconnjiflfimocjggfjdlmlhblm';

export const readerMode: ExtensionDefinition = {
  id: ID,
  name: 'Reader Mode',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'Broad WAR globs (img/*, css/*, *.html) on <all_urls> with no dynamic URL. ' +
    'Detection via a stable icon path is conclusive and CORS-free.',
  signals: [
    {
      id: 'war-icon-128-image',
      kind: 'web-accessible-resource',
      description:
        'Load img/icons/icon128.png via <img> — exposed to every origin and readable ' +
        'without a CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'img/icons/icon128.png'),
    },
    {
      id: 'war-icon-round-image',
      kind: 'web-accessible-resource',
      description:
        'Load img/icons/icon-round.png via <img> — a second icon corroborating the ' +
        'match and surviving either one being renamed.',
      weight: 0.5,
      run: () => probeImage(ID, 'img/icons/icon-round.png'),
    },
    {
      id: 'war-main-html',
      kind: 'web-accessible-resource',
      description: 'Fetch main.html — the reader view page, a third independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'main.html'),
    },
  ],
};
